import classNames from 'classnames';
import moment from 'moment';
import type Quill from 'quill/core';
import React, { useEffect, useMemo, useState } from 'react';
import type { SetNonNullable } from 'type-fest';
import type { VersionChange } from './calculateVersionChanges';
import * as styles from './EditFacesMagneticWall.module.scss';
import { useVersionChangesContext } from './VersionChanges';
import Avatar from '../../components/Avatar';
import Button from '../../components/Button';
import Tooltip from '../../components/Tooltip';
import Header from '../../editor/formats/Header';
import VersionChangeFormat from '../../editor/formats/VersionChange';
import { useActiveGutterBlot } from '../../editor/Gutter';
import type { HoveredBlotListenerSetting } from '../../editor/HoveredBlotListener';
import { useHoveredBlotListener } from '../../editor/HoveredBlotListener';
import { useQuill } from '../../editor/QuillContext';
import { getHeaderCollapse } from '../../editor/useHeaderCollapse';
import { useGetUser } from '../../user/useAllUsers';
import type { RelativeContentRects } from '../MagneticWallLayout';
import MagneticWallLayout, {
  useMagneticWallLayout,
} from '../MagneticWallLayout';
import getCollapsedHeaderRect from '../utils/getCollapsedHeaderRect';
import type { RelativeDOMRect } from '../utils/toRelativeDOMRect';
import toRelativeDOMRect from '../utils/toRelativeDOMRect';

const AVATAR_SIZE = parseInt(styles.avatarSize, 10);

interface EditFaceProps {
  versionChange: VersionChange;
  historyInsertedAt: Date;
  isInitiallyShowingTooltip?: boolean;
  onClick: () => void;
}

// eslint-disable-next-line import/no-unused-modules -- exported for testing
export const EditFace = ({
  versionChange,
  historyInsertedAt,
  isInitiallyShowingTooltip,
  onClick,
}: EditFaceProps) => {
  const getUser = useGetUser();
  const author = getUser({ id: versionChange.authorId });
  const date = moment(historyInsertedAt);

  return (
    <div className={styles.avatarContainer}>
      <Tooltip
        open={isInitiallyShowingTooltip}
        asChild
        content={
          <>
            <div>
              {date.isSame(moment(), 'year')
                ? date.format('MMM Do')
                : date.format('MMM Do YYYY')}
            </div>
            <Tooltip.Muted>{date.format('h:mma')}</Tooltip.Muted>
          </>
        }
      >
        <Button
          className={styles.avatarButton}
          aria-label={author.name}
          onClick={onClick}
        >
          <Avatar size={AVATAR_SIZE} user={author} />
        </Button>
      </Tooltip>
    </div>
  );
};

interface InnerProps {
  versionChanges: VersionChange[];
  historyInsertedAt: Date;
  hoveredBlotRect: RelativeDOMRect | null;
}

const Inner = ({
  versionChanges,
  historyInsertedAt,
  hoveredBlotRect,
}: InnerProps) => {
  const { layout, debouncedUpdateDesiredPositions, updateThreadHeights } =
    useMagneticWallLayout();
  const { focusedId, focusById } = useVersionChangesContext();

  const visibleItems = useMemo(
    () =>
      versionChanges
        .map((item) => {
          const isOverlapped = layout.overlappedIds.has(item.id);
          const top = layout.tops[item.id];

          if (top != null && hoveredBlotRect != null) {
            const bottom = top + AVATAR_SIZE;

            const isBelow = top > hoveredBlotRect.bottom;
            const isAbove = bottom < hoveredBlotRect.top;

            const isIntersectingAvatar = !isBelow && !isAbove;

            return {
              ...item,
              top,
              isInBackground: isIntersectingAvatar,
              isOverlapped,
            };
          }

          return {
            ...item,
            top,
            isInBackground: false,
            isOverlapped,
          };
        })
        .filter(
          (item): item is SetNonNullable<typeof item, 'top'> =>
            item.top != null,
        ),
    [layout.overlappedIds, layout.tops, versionChanges, hoveredBlotRect],
  );

  useEffect(() => {
    debouncedUpdateDesiredPositions();
    updateThreadHeights();
  }, [visibleItems, debouncedUpdateDesiredPositions, updateThreadHeights]);

  return (
    <>
      {visibleItems.map((versionChange) => {
        const isFocused = versionChange.id === focusedId;

        return (
          <MagneticWallLayout.Item
            key={versionChange.id}
            id={versionChange.id}
            focused={isFocused}
            top={versionChange.top}
            className={classNames({
              [styles.isInBackground]: versionChange.isInBackground,
            })}
          >
            <EditFace
              versionChange={versionChange}
              historyInsertedAt={historyInsertedAt}
              onClick={() => focusById(versionChange.id)}
            />
          </MagneticWallLayout.Item>
        );
      })}
    </>
  );
};

function isInlineElement(el: Element) {
  const d = getComputedStyle(el).display;
  return d === 'inline' || d.startsWith('inline-');
}

function getVersionChangeContentRects(quill: Quill): RelativeContentRects[] {
  const containerRect = quill.container.getBoundingClientRect();
  const collapsedHeaders =
    getHeaderCollapse(quill)?.getCollapsedHeaders() ?? [];

  return Array.from(
    quill.container.querySelectorAll<HTMLElement>(
      `[${VersionChangeFormat.keyName}]`,
    ),
  ).flatMap((element) => {
    const id = VersionChangeFormat.value(element) as string;

    const collapsedHeaderRect = getCollapsedHeaderRect(
      element,
      collapsedHeaders,
    );
    if (collapsedHeaderRect) {
      return [
        {
          rect: toRelativeDOMRect(collapsedHeaderRect, containerRect),
          id,
        },
      ];
    }

    const contentRect = element.getClientRects()[0];
    if (!contentRect) return [];

    const lineHeight = parseFloat(window.getComputedStyle(element).lineHeight);

    const finalContentRect = (() => {
      if (element instanceof HTMLParagraphElement) {
        return DOMRect.fromRect({
          x: contentRect.x + contentRect.width,
          y: contentRect.y + contentRect.height - lineHeight,
          width: 12,
          height: lineHeight,
        });
      }

      if (isInlineElement(element)) {
        // Line break diffs are 26px high, inline diffs are 24px high.
        // This means both could have different top values, but still be on the same line.
        // This can cause the ordering of the items in the magnetic wall layout to be incorrect.
        // In order to fix this, we normalize all content rects to top their line.
        return DOMRect.fromRect({
          ...contentRect,
          y: contentRect.y + contentRect.height / 2 - lineHeight / 2,
          height: lineHeight,
        });
      }

      return contentRect;
    })();

    return [
      {
        rect: toRelativeDOMRect(finalContentRect, containerRect),
        id,
      },
    ];
  });
}

const hoveredBlotListenerSetting: HoveredBlotListenerSetting<
  Record<string, unknown>,
  Header
> = {
  isHoveredBlot(blot) {
    return blot instanceof Header;
  },
};

interface EditFacesMagneticWallProps {
  historyInsertedAt: Date;
}

const EditFacesMagneticWall = ({
  historyInsertedAt,
}: EditFacesMagneticWallProps) => {
  const activeGutterBlot = useActiveGutterBlot();
  const { focusedId, versionChanges: editVersionChanges } =
    useVersionChangesContext();

  const { quill } = useQuill();

  const focus = useMemo(
    () =>
      focusedId
        ? {
            id: focusedId,
            active: true,
          }
        : null,
    [focusedId],
  );

  const [hoveredBlotRect, setHoveredBlotRect] =
    useState<RelativeDOMRect | null>(null);

  const { hoveredBlot: hoveredHeader } = useHoveredBlotListener(
    quill,
    hoveredBlotListenerSetting,
  );

  const hoveredBlot = activeGutterBlot ?? hoveredHeader;

  useEffect(() => {
    if (!quill) return;

    if (hoveredBlot) {
      setHoveredBlotRect(
        toRelativeDOMRect(
          hoveredBlot.domNode.getBoundingClientRect(),
          quill.container.getBoundingClientRect(),
        ),
      );
    } else {
      setHoveredBlotRect(null);
    }
  }, [hoveredBlot, quill]);

  return (
    <div
      className={classNames(styles.container, {
        [styles.hasHoveredBlot]: hoveredBlot != null,
      })}
    >
      <MagneticWallLayout.Root
        itemSize="gutter"
        magneticWallItems={editVersionChanges}
        focus={focus}
        getRelativeContentRects={() =>
          quill ? getVersionChangeContentRects(quill) : []
        }
      >
        <Inner
          versionChanges={editVersionChanges}
          hoveredBlotRect={hoveredBlotRect}
          historyInsertedAt={historyInsertedAt}
        />
      </MagneticWallLayout.Root>
    </div>
  );
};

export default EditFacesMagneticWall;
